/**
 * Filters the info cards data to find the card matching the specified position.
 * @param {Array} infoCardsData - List of info card objects.
 * @param {string} positionType - The type of position to check (e.g., 'position_desktop').
 * @param {number} position - The position to match.
 * @returns {Object|null} The matching info card or null if not found.
 */
const getInfoCardByPosition = (infoCardsData, positionType, position) => {
  const cards = infoCardsData.filter(card => card[positionType] === position);
  return cards.length > 0 ? cards[0] : null;
};

/**
 * Retrieves the image data for a specific device type and position.
 * @param {string} deviceType - The device type ('desktop' or 'mobile').
 * @param {number} position - The position of the image.
 * @returns {Object} The image data for the specified device type and position.
 */
const getImageByDeviceType = (deviceType, position) => {
  const infoCardsImagesData = window.infoCardsImages;
  return infoCardsImagesData[deviceType][position];
};

/**
 * Determines the position of the info card based on the device type.
 * @param {Object} infoCardContent - The content of the info card.
 * @param {string} deviceType - The device type ('desktop' or 'mobile').
 * @returns {number} The position of the info card for the given device type.
 */
const getPosition = (infoCardContent, deviceType) => {
  return deviceType === 'desktop' ? infoCardContent.position_desktop : infoCardContent.position_mobile;
};

/**
 * Creates an info card element based on the provided content and device type.
 * @param {Object} infoCardContent - The content of the info card.
 * @param {string} deviceType - The device type ('desktop' or 'mobile').
 * @param {number} position - The position of the info card.
 * @returns {HTMLLIElement} The created info card element.
 */
const createInfoCardElement = (infoCardContent, deviceType, position) => {
  const image = getImageByDeviceType(deviceType, position);
  const li = document.createElement('li');
  li.className = `grid__item product-card plp-info-card--${deviceType} ${(deviceType === 'mobile' && infoCardContent.mobile_is_double_space === 'true') ? 'span-two' : ''}`;
  li.setAttribute('data-plp-info-card', `${deviceType}-${getPosition(infoCardContent, deviceType)}`);
  const imageElement = (deviceType === 'mobile' && image.mobile_image !== '')
    ? ` <div class="plp-info-card--${deviceType}_link-img-mobile"><img src="${image.mobile_image}" alt="${infoCardContent.title}" /></div>`
    : `<div class="plp-info-card--${deviceType}_link-img-desktop"><img src="${image.desktop_image}" alt="${infoCardContent.title}" /></div>`;
  li.innerHTML = `
        <a class="plp-info-card--${deviceType}_link" href="${infoCardContent.link}">
          <div class="plp-info-card--${deviceType}_link-img">
            ${imageElement}
          </div>
        </a>`;
  return li;
};

/**
 * Renders the content of an info card and inserts it after a specified child element.
 * @param {string} deviceType - The device type ('desktop' or 'mobile').
 * @param {Object} infoCardContent - The content of the info card.
 * @param {number} position - The position of the info card.
 * @param {Element} child - The child element to insert the info card after.
 */
const renderInfoCardContent = (deviceType, infoCardContent, position, child) => {
  const infoCardElement = createInfoCardElement(infoCardContent, deviceType, position);
  child.insertAdjacentElement('afterend', infoCardElement);
};

/**
 * Renders info cards for the given product grid node.
 * @param {Element} node - The product grid node.
 */
const renderInfoCard = node => {
  const infoCardsData = window.infoCards;
  if (infoCardsData) {
    const currentChildren = Array.from(node.children);
    currentChildren
      .filter(child => !child.hasAttribute('data-plp-info-card'))
      .forEach((child, index) => {
        const position = index + 1;
        const desktopInfoCard = getInfoCardByPosition(infoCardsData, 'position_desktop', position);
        const mobileInfoCard = getInfoCardByPosition(infoCardsData, 'position_mobile', position);

        if (desktopInfoCard && !document.querySelector(`[data-plp-info-card="desktop-${desktopInfoCard.position_desktop}"]`)) {
          if (desktopInfoCard.position_desktop === position) {
            renderInfoCardContent('desktop', desktopInfoCard, position, child);
          }
        }
        if (mobileInfoCard && !document.querySelector(`[data-plp-info-card="mobile-${mobileInfoCard.position_mobile}"]`)) {
          if (mobileInfoCard.position_mobile === position) {
            renderInfoCardContent('mobile', mobileInfoCard, position, child);
          }
        }
      });
  }
};

/**
 * Entry point for the info cards module.
 */
export const infoCards = () => {
  document.addEventListener('newitems', ()=> {
    const productGrid = document.getElementById('product-grid');
    const enableInfoCard = window.enableInfoCards;
    if (productGrid && enableInfoCard) {
      renderInfoCard(productGrid);
    }
  })
};
